// ============================================================================
// AUDIT FIRM FORM MODAL — FSA Signature Workflow
// ============================================================================
// Create/Edit master form for fsa.tbl_audit_firm. Follows the standard
// Form Modal Blueprint (unsaved changes tracking, inline validation,
// 3-stage save flow with ConfirmDialog + loading + toast).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditFirm } from 'api/types/fsa/signature.types';

export interface AuditFirmFormData {
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText: string;
  signatureBlockText: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  /**
   * ADR-0024 A-2 — BLANK_SPACE = this firm prints on pre-printed stationery
   * (overrides the house style); TEXT = follow the house style in Document
   * Output Settings → Auditor's Documents. (LOGO_TEXT is legacy — same as TEXT.)
   */
  letterheadMode: 'TEXT' | 'LOGO_TEXT' | 'BLANK_SPACE';
  /** Firm logo as a base64 data URI ('' = none). */
  logoImageBase64: string;
  gstin: string;
  /** '#RRGGBB' or '' (default navy when the house style uses the firm colour). */
  accentColor: string;
}

const INITIAL_FORM_DATA: AuditFirmFormData = {
  firmName: '',
  firmRegistrationNumber: '',
  reportAttestationText: 'As per our Report of even date attached',
  signatureBlockText: '',
  address: '',
  email: '',
  phone: '',
  website: '',
  letterheadMode: 'TEXT',
  logoImageBase64: '',
  gstin: '',
  accentColor: '',
};

const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;

/** ~300 KB image cap — matches the backend's 600K-char base64 limit with headroom. */
const LOGO_MAX_BYTES = 300 * 1024;

interface AuditFirmFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: AuditFirm | null;
  onSaved: () => void;
  onClose: () => void;
  // ---- Minimize-to-bar support ----
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordId?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const AuditFirmFormModal: React.FC<AuditFirmFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<AuditFirmFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Populate form on open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const data: AuditFirmFormData = recordRef.current ? {
      firmName:               recordRef.current.firmName,
      firmRegistrationNumber: recordRef.current.firmRegistrationNumber,
      reportAttestationText:  recordRef.current.reportAttestationText ?? '',
      signatureBlockText:     recordRef.current.signatureBlockText ?? '',
      address:                recordRef.current.address ?? '',
      email:                  recordRef.current.email ?? '',
      phone:                  recordRef.current.phone ?? '',
      website:                recordRef.current.website ?? '',
      // LOGO_TEXT (legacy) now means the same as TEXT — follow the house style.
      letterheadMode:         recordRef.current.letterheadMode === 'BLANK_SPACE' ? 'BLANK_SPACE' : 'TEXT',
      logoImageBase64:        recordRef.current.logoImageBase64 ?? '',
      gstin:                  recordRef.current.gstin ?? '',
      accentColor:            recordRef.current.accentColor ?? '',
    } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler (captures this session's data at minimize time) ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecordId = recordRef.current ? String(recordRef.current.id) : undefined;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`
      : `${tabId}-form-modal-edit-${currentRecordId || 'new'}`;

    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.firmName.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.firmName || 'Audit Firm'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(savedFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecordId : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // Logo upload — file → base64 data URI, PNG/JPG only, ~300 KB cap.
  // Stored in-row (no file-store infrastructure in FSA); the report PDF
  // strips the data-URI prefix server-side before rendering.
  const handleLogoFile = useCallback((file: File | null) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      setFieldErrors(prev => ({ ...prev, logoImageBase64: 'Only PNG or JPG images are supported' }));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setFieldErrors(prev => ({ ...prev, logoImageBase64: 'Image is too large — keep it under 300 KB' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      clearFieldError('logoImageBase64');
      setFormData(prev => ({ ...prev, logoImageBase64: String(reader.result ?? '') }));
    };
    reader.onerror = () => {
      setFieldErrors(prev => ({ ...prev, logoImageBase64: 'Could not read the image file' }));
    };
    reader.readAsDataURL(file);
  }, [clearFieldError]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.firmName.trim())               errors.firmName = 'Firm name is required';
    if (!formData.firmRegistrationNumber.trim()) errors.firmRegistrationNumber = 'FRN is required';
    if (formData.firmName.length > 200)          errors.firmName = 'Firm name cannot exceed 200 characters';
    if (formData.firmRegistrationNumber.length > 20) errors.firmRegistrationNumber = 'FRN cannot exceed 20 characters';
    if (formData.accentColor && !HEX_COLOUR.test(formData.accentColor)) errors.accentColor = 'Use a colour like #1E3A5F';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        firmName:               formData.firmName.trim(),
        firmRegistrationNumber: formData.firmRegistrationNumber.trim(),
        reportAttestationText:  formData.reportAttestationText.trim() || null,
        signatureBlockText:     formData.signatureBlockText.trim() || null,
        address:                formData.address.trim() || null,
        email:                  formData.email.trim() || null,
        phone:                  formData.phone.trim() || null,
        website:                formData.website.trim() || null,
        letterheadMode:         formData.letterheadMode,
        logoImageBase64:        formData.logoImageBase64 || null,
        gstin:                  formData.gstin.trim().toUpperCase() || null,
        accentColor:            formData.accentColor.trim().toUpperCase() || null,
        statusCode:             'ACTIVE',
      };
      if (mode === 'create') {
        await fsaAuditFirmService.create(payload);
      } else if (recordRef.current) {
        await fsaAuditFirmService.update(recordRef.current.id, {
          ...payload,
          rowVersion: recordRef.current.rowVersion,
        });
      }
      setIsSaving(false);
      notificationService.toast.success(`"${formData.firmName}" ${mode === 'create' ? 'created' : 'updated'} successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const msg = err?.response?.data?.message ?? 'Save failed';
      notificationService.toast.error(msg, 6000);
    }
  }, [mode, formData, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Audit Firm' : 'Edit Audit Firm'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving} title="Close"><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="firmName">
              <label className="form-label">Firm Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('firmName') ? ' form-field-error' : ''}`}
                value={formData.firmName}
                onChange={(e) => { clearFieldError('firmName'); setFormData(prev => ({ ...prev, firmName: e.target.value })); }}
                maxLength={200}
              />
              {getFieldError('firmName') && <div className="form-error-message">{getFieldError('firmName')}</div>}
            </div>
            <div className="form-group" data-field="firmRegistrationNumber">
              <label className="form-label">Firm Registration Number (FRN) <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('firmRegistrationNumber') ? ' form-field-error' : ''}`}
                value={formData.firmRegistrationNumber}
                onChange={(e) => { clearFieldError('firmRegistrationNumber'); setFormData(prev => ({ ...prev, firmRegistrationNumber: e.target.value })); }}
                placeholder="e.g. 101248W/W-100022"
                maxLength={20}
              />
              {getFieldError('firmRegistrationNumber') && <div className="form-error-message">{getFieldError('firmRegistrationNumber')}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Report Attestation Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.reportAttestationText}
              onChange={(e) => setFormData(prev => ({ ...prev, reportAttestationText: e.target.value }))}
              maxLength={500}
              placeholder="As per our Report of even date attached"
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Auditor preamble line printed above the auditor's signature under the financial statements (not on the Auditor's Report itself). Default ICAI wording shown.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Signature Block Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.signatureBlockText}
              onChange={(e) => setFormData(prev => ({ ...prev, signatureBlockText: e.target.value }))}
              maxLength={500}
              placeholder={formData.firmName ? `For ${formData.firmName}` : 'For {firm name}'}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Firm-name heading printed at the top of the signature block. Empty falls back to "For {'{firm name}'}" — typical Indian FS convention.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="form-grid-3">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="text"
                className="form-input"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                maxLength={50}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input
                type="text"
                className="form-input"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                maxLength={200}
              />
            </div>
          </div>

          {/* ADR-0024 A-2 — the firm's letterhead DETAILS. How it looks
              (Classic / Modern, colour, first page only …) is the house
              style in Document Output Settings → Auditor's Documents. */}
          <div className="form-grid-2">
            <div className="form-group" data-field="gstin">
              <label className="form-label">GSTIN</label>
              <input
                type="text"
                className="form-input"
                value={formData.gstin}
                onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value }))}
                maxLength={20}
                placeholder="Printed in the letterhead footer"
              />
            </div>
            <div className="form-group" data-field="accentColor">
              <label className="form-label">Firm colour</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={HEX_COLOUR.test(formData.accentColor) ? formData.accentColor : '#1E3A5F'}
                  onChange={(e) => { clearFieldError('accentColor'); setFormData(prev => ({ ...prev, accentColor: e.target.value.toUpperCase() })); }}
                  style={{ width: 40, height: 32, padding: 0, border: '1px solid var(--border-color)', borderRadius: 4 }}
                  title="Pick the firm colour"
                />
                <input
                  type="text"
                  className={`form-input${getFieldError('accentColor') ? ' form-field-error' : ''}`}
                  value={formData.accentColor}
                  onChange={(e) => { clearFieldError('accentColor'); setFormData(prev => ({ ...prev, accentColor: e.target.value })); }}
                  placeholder="#1E3A5F (optional)"
                  maxLength={7}
                  style={{ flex: 1 }}
                />
              </div>
              {getFieldError('accentColor') && <div className="form-error-message">{getFieldError('accentColor')}</div>}
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '-0.25rem 0 0.75rem' }}>
            The colour is used for the letterhead only when Document Output Settings → Auditor's Documents → Letterhead colour is "Firm colour".
          </div>

          <div className="form-group" data-field="letterheadMode">
            <label className="form-label">Letterhead on the Auditor's Report and CARO</label>
            <select
              className="form-select"
              value={formData.letterheadMode === 'BLANK_SPACE' ? 'BLANK_SPACE' : 'TEXT'}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                letterheadMode: e.target.value as AuditFirmFormData['letterheadMode'],
              }))}
            >
              <option value="TEXT">Printed letterhead (house style)</option>
              <option value="BLANK_SPACE">Pre-printed stationery — leave the space blank</option>
            </select>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              The house style (Classic / Modern, first page only, colour) is set in Document Output Settings → Auditor's Documents.
              Pre-printed stationery overrides it for this firm.
            </div>
          </div>

          {(
            <div className="form-group" data-field="logoImageBase64">
              <label className="form-label">Firm Logo (e.g. CA India logo)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {formData.logoImageBase64 && (
                  <img
                    src={formData.logoImageBase64}
                    alt="Firm logo preview"
                    style={{
                      height: 44, maxWidth: 120, objectFit: 'contain',
                      border: '1px solid var(--border-color)', borderRadius: 4,
                      background: '#ffffff', padding: 2,
                    }}
                  />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="form-input"
                  style={{ flex: 1 }}
                  onChange={(e) => { handleLogoFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
                />
                {formData.logoImageBase64 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setFormData(prev => ({ ...prev, logoImageBase64: '' }))}
                    title="Remove the uploaded logo"
                  >
                    Remove
                  </button>
                )}
              </div>
              {getFieldError('logoImageBase64') && <div className="form-error-message">{getFieldError('logoImageBase64')}</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                PNG/JPG, up to 300 KB. Printed in the letterhead when "Show logo" is on in Document Output Settings.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} audit firm "${formData.firmName}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default AuditFirmFormModal;
